import React from "react";

/**
 * Monoline outlined icons used by the SettingsTab "App" and
 * "Help & legal" sections only. The Account / Subscription /
 * Refer-a-friend sections keep their original styling (gold PRO shield
 * PNG, gold coin stack PNG, gold gift PNG, navy avatar) because the
 * user explicitly wanted those preserved.
 *
 * Design contract:
 *   - Single stroke color: #4A5D78 (navy-soft) — matches BottomNav
 *     inactive icons.
 *   - strokeWidth 1.8, rounded line caps, fill="none" everywhere.
 *   - viewBox 0 0 24 24 so every glyph reads at the same optical
 *     weight when scaled to 22-34px Section sizes.
 *   - Each icon accepts an optional `size` prop. Extra props passed by
 *     Row (`weight`, `color`, etc.) are silently ignored so swapping a
 *     Phosphor icon for one of these is a no-touch substitution.
 */

const STROKE = "#4A5D78";

const baseProps = (size) => ({
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  strokeLinecap: "round",
  strokeLinejoin: "round",
});

// 🌐 Globe — Language row.
export function GlobeOutlineIcon({ size = 22 }) {
  return (
    <svg {...baseProps(size)} aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke={STROKE} strokeWidth={1.8} />
      <ellipse cx="12" cy="12" rx="4" ry="9" stroke={STROKE} strokeWidth={1.8} />
      <path d="M3 12h18" stroke={STROKE} strokeWidth={1.8} />
    </svg>
  );
}

// 💳 Credit card — Home currency + Payments.
export function CreditCardOutlineIcon({ size = 22 }) {
  return (
    <svg {...baseProps(size)} aria-hidden="true">
      <rect x="3" y="6" width="18" height="13" rx="2.5" stroke={STROKE} strokeWidth={1.8} />
      <path d="M3 11h18" stroke={STROKE} strokeWidth={1.8} />
      <path d="M15 15.5h3" stroke={STROKE} strokeWidth={1.6} />
    </svg>
  );
}

// 🖼 Picture — Tip image watermark.
export function ImageOutlineIcon({ size = 22 }) {
  return (
    <svg {...baseProps(size)} aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2.5" stroke={STROKE} strokeWidth={1.8} />
      <path d="M4.5 17l4.5-5 3.5 4 2.5-2.5 4.5 5" stroke={STROKE} strokeWidth={1.8} />
      <circle cx="15.5" cy="9" r="1.4" stroke={STROKE} strokeWidth={1.6} />
    </svg>
  );
}

// 🛡✓ Shield with check — Vault security.
export function ShieldCheckOutlineIcon({ size = 22 }) {
  return (
    <svg {...baseProps(size)} aria-hidden="true">
      <path
        d="M12 3l7 2.5v5.5c0 4.5-3.2 8.4-7 9.5-3.8-1.1-7-5-7-9.5V5.5L12 3z"
        stroke={STROKE}
        strokeWidth={1.8}
      />
      <path d="M8.5 12l2.5 2.5 4.5-4.5" stroke={STROKE} strokeWidth={1.8} />
    </svg>
  );
}

// 🔊 Speaker — Voice rows.
export function SpeakerOutlineIcon({ size = 22 }) {
  return (
    <svg {...baseProps(size)} aria-hidden="true">
      <path d="M4 9.5h3l4-3v11l-4-3H4v-5z" stroke={STROKE} strokeWidth={1.8} />
      <path d="M15 9.5a3.5 3.5 0 010 5" stroke={STROKE} strokeWidth={1.8} />
      <path d="M17.5 7a7 7 0 010 10" stroke={STROKE} strokeWidth={1.8} />
    </svg>
  );
}

// ❓ Help — Help center row.
export function QuestionOutlineIcon({ size = 22 }) {
  return (
    <svg {...baseProps(size)} aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke={STROKE} strokeWidth={1.8} />
      <path
        d="M9 9.5a3 3 0 016 0c0 1.6-1.5 2.3-2.4 2.8-.6.4-.6 1-.6 1.7"
        stroke={STROKE}
        strokeWidth={1.8}
      />
      <circle cx="12" cy="17" r="0.8" fill={STROKE} stroke={STROKE} strokeWidth={0.5} />
    </svg>
  );
}

// ⓘ Info — About + Terms rows.
export function InfoOutlineIcon({ size = 22 }) {
  return (
    <svg {...baseProps(size)} aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke={STROKE} strokeWidth={1.8} />
      <path d="M12 11v5.5" stroke={STROKE} strokeWidth={1.8} />
      <circle cx="12" cy="8" r="0.9" fill={STROKE} stroke={STROKE} strokeWidth={0.5} />
    </svg>
  );
}

// 🔒 Shield with padlock — Privacy policy row.
export function ShieldLockOutlineIcon({ size = 22 }) {
  return (
    <svg {...baseProps(size)} aria-hidden="true">
      <path
        d="M12 3l7 2.5v5.5c0 4.5-3.2 8.4-7 9.5-3.8-1.1-7-5-7-9.5V5.5L12 3z"
        stroke={STROKE}
        strokeWidth={1.8}
      />
      <rect x="9.5" y="11.2" width="5" height="4.6" rx="0.7" stroke={STROKE} strokeWidth={1.6} />
      <path d="M10.5 11.2V9.6a1.5 1.5 0 013 0v1.6" stroke={STROKE} strokeWidth={1.6} />
    </svg>
  );
}

// 🗑 Trash — Clear data row.
export function TrashOutlineIcon({ size = 22 }) {
  return (
    <svg {...baseProps(size)} aria-hidden="true">
      <path d="M4 7h16" stroke={STROKE} strokeWidth={1.8} />
      <path
        d="M6.5 7l1 12a2 2 0 002 1.8h5a2 2 0 002-1.8l1-12"
        stroke={STROKE}
        strokeWidth={1.8}
      />
      <path
        d="M9.5 7V5a1.5 1.5 0 011.5-1.5h2A1.5 1.5 0 0114.5 5v2"
        stroke={STROKE}
        strokeWidth={1.8}
      />
      <path d="M10 11v6M14 11v6" stroke={STROKE} strokeWidth={1.6} />
    </svg>
  );
}
